import React, { useCallback, useState } from 'react';
import { RedirectProps } from 'react-router-dom';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Header, HeaderProps } from '../../../components/organisms/Header/Header';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import {
  getAlphabeticalProviderGroups,
  ProviderViewSet,
  sortAlphabeticalProviderGroups,
} from '../../../utilities/provider-view-set';
import {
  findProviderViewSetGroupByTitle,
  searchProviders,
} from '../../../utilities/provider-utils';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { PageView } from '../../../models/analytics';

import {
  ProviderListHeader as ProviderListHeaderImport,
  ProviderListHeaderProps,
} from '../../components/ProvidersList/ProviderListHeader';
import {
  ProviderListProps,
  ProvidersList as ProvidersListImport,
  SelectProviderViewSetGroup,
} from '../../components/ProvidersList/ProvidersList';
import {
  ProviderViewSetArgs,
  useProviderSearch as useProviderSearchImport,
} from '../ProviderGroupPicker/useProviderSearch';
import { commonMessages } from '../../../components/atoms/CommonMessages/messages';
import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { ProviderEntity } from '../../../models/provider';
import { usePageViewEvents as usePageViewEventsImport } from '../../Analytics/usePageViewEvents';
import { FlexBox } from '../../../components/atoms/FlexBox/FlexBox';
import { StyledProviderIcon } from '../../components/ProvidersList/ProvidersList.styles';
import { useGradualRollout as useGradualRolloutImport } from '../../../utilities/useGradualRollout';
import { GradualReleaseLabels } from '../../../utilities/useGradualRolloutTypes';
import { BranchList } from '../../components/BranchList/BranchList';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { selectUser } from '../../Analytics/new-analytics';
import { Text } from '../../../components/atoms/Text/Text';
import { messages } from './messages';

export const searchIndividualProvidersFn = (
  { providers, version }: ProviderViewSetArgs,
  searchString: string
) => {
  const trimmedSearchString = searchString.trim();

  if (trimmedSearchString.length > 0) {
    return searchProviders({
      providers,
      searchKeys: ['displayName'],
      searchString: trimmedSearchString,
      version,
    });
  }

  const providerViewSet = new ProviderViewSet(providers, version);

  return providerViewSet.groupByDisplayName();
};

/** This function is used for Sparkasse region mapping. It makes sure that we filter on region. But also keep the ability to search "globally" in the Sparkasse group */
export const searchIndividualProvidersWithFilter =
  (selectedRegionName: string, regionMappings: Record<string, string>) =>
  ({ providers, version }: ProviderViewSetArgs, searchString: string) => {
    const trimmedSearchString = searchString.trim();
    const shouldFilterByRegion = trimmedSearchString === '';

    let filteredProviders = providers;

    if (shouldFilterByRegion) {
      filteredProviders = filteredProviders.filter(
        (provider) => regionMappings[provider.name] === selectedRegionName
      );
    }

    return searchIndividualProvidersFn({ providers: filteredProviders, version }, searchString);
  };

export const getSearchProvidersStrategy = (
  selectedRegionName?: string,
  regionMappings?: Record<string, string>
) => {
  if (selectedRegionName && regionMappings) {
    return searchIndividualProvidersWithFilter(selectedRegionName, regionMappings);
  }

  return searchIndividualProvidersFn;
};

export const getActiveProviderGroup = (
  { providers, version }: ProviderViewSetArgs,
  groupDisplayName: string
) => {
  const providerViewSet = new ProviderViewSet(providers, version);
  const groupDisplayNameGroups = providerViewSet.groupByGroupDisplayName();

  return findProviderViewSetGroupByTitle(groupDisplayNameGroups, groupDisplayName);
};

export type FinancialInstitutionPickerProps = {
  onBack: () => void;
  groupDisplayName: string;
  providers: ProviderEntity[];
  searchIndividualProviders?: typeof searchIndividualProvidersFn;
  onProviderItemClick: SelectProviderViewSetGroup;
  ProviderListHeader?: React.ComponentType<ProviderListHeaderProps>;
  ProvidersList?: React.ComponentType<ProviderListProps>;
  RedirectComponent?: React.ComponentType<RedirectProps>;
  HeaderComponent?: React.ComponentType<HeaderProps>;
  usePageViewEvents?: ({ pageView }: { pageView: PageView }) => void;
  useProviderSearch?: typeof useProviderSearchImport;
  useGradualRollout?: typeof useGradualRolloutImport;
};

type BranchHeaderProps = { iconUrl?: string; groupDisplayName: string };
const BranchHeader = ({ iconUrl, groupDisplayName }: BranchHeaderProps) => (
  <FlexBox mb="normal" alignItems="center">
    {iconUrl && (
      <StyledProviderIcon
        height="40"
        width="40"
        src={iconUrl}
        alt=""
        role="presentation"
        loading="lazy"
      />
    )}
    <Text as="h2" size="small" bold ml="xsmall">
      <FormattedMessage {...messages.branchHeader} values={{ groupDisplayName }} />
    </Text>
  </FlexBox>
);

export const FinancialInstitutionPicker: React.FC<FinancialInstitutionPickerProps> = ({
  onBack,
  onProviderItemClick,
  groupDisplayName,
  providers,
  searchIndividualProviders = searchIndividualProvidersFn,
  ProviderListHeader = ProviderListHeaderImport,
  ProvidersList = ProvidersListImport,
  HeaderComponent = Header,
  usePageViewEvents = usePageViewEventsImport,
  useProviderSearch = useProviderSearchImport,
  useGradualRollout = useGradualRolloutImport,
}) => {
  const searchFunction = searchIndividualProviders;
  const { market, clientId } = useUrlQuery();
  const user = useSelector(selectUser);
  const intl = useIntl();
  const {
    version: { version },
  } = useVersion();
  const pageView = PageView.PROVIDER_GROUP_SELECTION_SCREEN;
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>('');

  const groupDisplayNameGroup = getActiveProviderGroup({ providers, version }, groupDisplayName);
  const { providerViewSetGroups, onSearchChange, sendSearchHistoryAnalytic } = useProviderSearch({
    pageView,
    providerViewSetArgs: { providers: groupDisplayNameGroup?.providers || [], version },
    filterFn: searchFunction,
    delay: 275,
  });

  const handleSearchChange = useCallback(
    (searchString: string) => {
      setCurrentSearchQuery(searchString);
      onSearchChange(searchString);
    },
    [onSearchChange]
  );

  usePageViewEvents({ pageView });

  const { isInTestGroup } = useGradualRollout(
    PageView.PROVIDER_GROUP_SELECTION_SCREEN,
    GradualReleaseLabels.DEFAULT_FLOWS_BRANCH_SELECTION_AB_50
  );
  const providersInGroup = groupDisplayNameGroup?.providers || [];
  const providerInGroup = providersInGroup.length > 0 ? providersInGroup[0] : undefined;
  const inTestGroup = isInTestGroup({
    userId: user?.id || '',
    market,
    provider: providerInGroup,
    clientId,
  });

  if (inTestGroup) {
    const iconUrl = providerInGroup?.images.icon;
    const searchQueryIsEmpty = currentSearchQuery.length === 0;
    // When no search is made, we sort on alphabetical order by both the keys, and the inner provider branch list.
    // When the user searches we should let the fuzzy search results dictate the order of the alphabetical groups.
    const alphabeticalProviderGroups = searchQueryIsEmpty
      ? sortAlphabeticalProviderGroups(getAlphabeticalProviderGroups(providerViewSetGroups))
      : getAlphabeticalProviderGroups(providerViewSetGroups);

    return (
      <ScreenWrapper>
        <HeaderComponent onBack={onBack} pageView={PageView.PROVIDER_GROUP_SELECTION_SCREEN} />
        <ContentBody fullWidth>
          <ProviderListHeader
            placeholder={intl.formatMessage(commonMessages.search)}
            onSearchChange={handleSearchChange}
            headerMessage={messages.selectYourBranch}
          >
            <BranchHeader iconUrl={iconUrl} groupDisplayName={groupDisplayName} />
          </ProviderListHeader>
          <BranchList
            currentSearchQuery={currentSearchQuery}
            providerViewSetGroups={alphabeticalProviderGroups}
            onProviderItemClick={(payload) => {
              onProviderItemClick(payload);
              sendSearchHistoryAnalytic();
            }}
          />
        </ContentBody>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <HeaderComponent onBack={onBack} pageView={PageView.PROVIDER_GROUP_SELECTION_SCREEN} />
      <ContentBody fullWidth>
        <ProviderListHeader
          placeholder={intl.formatMessage(commonMessages.search)}
          onSearchChange={handleSearchChange}
          headerMessage={messages.selectYourBranch}
        />
        <ProvidersList
          currentSearchQuery={currentSearchQuery}
          providerViewSetGroups={providerViewSetGroups}
          onProviderItemClick={(payload) => {
            onProviderItemClick(payload);
            sendSearchHistoryAnalytic();
          }}
        />
      </ContentBody>
    </ScreenWrapper>
  );
};
